import { Head } from "vite-react-ssg";
import { Hero } from "@/components/Hero";
import { SocialProof } from "@/components/SocialProof";
import { CtaBand } from "@/components/CtaBand";
import { LoopDiagram } from "@/components/LoopDiagram";
import { DomainStory } from "@/components/DomainStory";
import { Section, SectionHeading, Stat, Pill } from "@/components/kit";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import aiAgent from "@/assets/shots/ai-agent.png";
import {
  CONCEPT,
  PILLARS,
  DOMAIN_STORIES,
  STATS,
  AI_TEASER,
  DEMO_DOMAINS,
  TRUST_ITEMS,
} from "@/content/home";

export default function Home() {
  return (
    <>
      <Head>
        <title>DjiniousLab — Model-based design on a digital replica</title>
        <meta
          name="description"
          content="DjiniousLab is one engineering notebook for model-based design: model your system as a digital replica, simulate and test it, then generate the embedded software and physical parts that ship."
        />
      </Head>

      {/* 1. Hero */}
      <Hero />

      {/* 2. Social proof */}
      <SocialProof />

      {/* 3. The big idea — digital replica + the loop */}
      <Section wash="wash">
        <SectionHeading eyebrow={CONCEPT.eyebrow} title={CONCEPT.title} intro={CONCEPT.intro} />
        <div className="mt-12">
          <LoopDiagram />
        </div>
      </Section>

      {/* 4. The three pillars */}
      <Section>
        <SectionHeading
          eyebrow="THE THREE PILLARS"
          title="Model & design. Simulate & test. Generate."
          intro="Every capability in DjiniousLab serves one of three jobs — and together they close the loop from idea to shipped artefact."
        />
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {PILLARS.map((p) => (
            <Card key={p.id} className="flex flex-col gap-4">
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-sm font-bold text-primary">{p.step}</span>
                <h3 className="text-lg font-extrabold tracking-tight text-foreground">{p.title}</h3>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              <ul className="mt-1 flex flex-col gap-2">
                {p.points.map((pt) => (
                  <li
                    key={pt}
                    className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      {/* 5. Domain story band */}
      <Section wash="surface">
        <SectionHeading
          eyebrow="ONE NOTEBOOK, ANY SYSTEM"
          title="The same loop, across every domain."
          intro="A digital replica doesn't care whether it models an aircraft, a vehicle, or a factory cell. Each of these started as a model, was proven in simulation, and generated something that ships."
        />
        <div className="mt-16 flex flex-col gap-16">
          {DOMAIN_STORIES.map((story, i) => (
            <DomainStory key={story.domain} story={story} flip={i % 2 === 1} />
          ))}
        </div>
      </Section>

      {/* 6. Stat strip */}
      <Section bordered>
        <div className="grid grid-cols-2 gap-y-10 sm:grid-cols-4">
          {STATS.map((s) => (
            <Stat key={s.label} value={s.value} label={s.label} />
          ))}
        </div>
      </Section>

      {/* 7. AI assistant teaser */}
      <Section wash="surface">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="flex flex-col gap-5">
            <SectionHeading
              align="left"
              eyebrow={AI_TEASER.eyebrow}
              title={AI_TEASER.title}
              intro={AI_TEASER.intro}
            />
            <div>
              <Button to={AI_TEASER.cta.to}>{AI_TEASER.cta.label}</Button>
            </div>
          </div>
          <figure className="mx-auto w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-card shadow-lift">
            <img
              src={aiAgent}
              alt="The DjiniousLab in-app AI assistant answering 'which notebooks render 3D plots' — it calls the list_notebooks tool and returns a structured table, behind the same approval gate the UI uses"
              className="block w-full"
              loading="lazy"
            />
          </figure>
        </div>
      </Section>

      {/* 8. Demo domains strip */}
      <Section>
        <div className="flex flex-col items-center gap-8 text-center">
          <SectionHeading
            eyebrow="PROVEN BREADTH"
            title="From drone autopilots to reactor kinetics."
            intro="Over 130 ready-to-run demos spanning nine engineering disciplines. Every demo is a digital replica you can open and run — seed data, expected outputs, and a companion notebook included."
          />
          <div className="flex flex-wrap justify-center gap-3">
            {DEMO_DOMAINS.map((d) => (
              <Pill key={d.label} tone={d.tone}>
                {d.label}
              </Pill>
            ))}
          </div>
          <Button to="/demos">Explore the demos</Button>
        </div>
      </Section>

      {/* 9. Enterprise trust strip */}
      <Section wash="surface">
        <SectionHeading
          eyebrow="BUILT FOR TEAMS"
          title="Ready for production from day one."
          intro="DjiniousLab ships with the compliance and observability primitives that enterprise teams require — no bolt-on security or custom integrations needed."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {TRUST_ITEMS.map((item) => (
            <Card key={item.title} className="flex flex-col gap-2">
              <h3 className="text-sm font-bold text-foreground">{item.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{item.body}</p>
            </Card>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Button to="/enterprise" variant="ghost">
            Enterprise overview
          </Button>
        </div>
      </Section>

      {/* 10. Closing CTA band */}
      <CtaBand />
    </>
  );
}
